import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Edit, QrCode, Download, Printer, Barcode } from 'lucide-react';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { PageHeader } from '@/components/shared/PageHeader';
import { TablePagination } from '@/components/shared/TablePagination';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import { usePaginationFromUrl } from '@/hooks/usePaginationFromUrl';
import { useAuth } from '@/hooks/useAuth';
import { useTenantSwitcher } from '@/contexts/TenantSwitcherContext';
import { getItemById } from '@/services/itemService';
import { getShelfById, listShelves } from '@/services/shelfService';
import { getDetectionsByItemIdPaginated } from '@/services/detectionService';
import { listBranches } from '@/services/branchService';
import { listCategories } from '@/services/categoryService';
import { listScanners } from '@/services/scannerService';
import { listCustomFieldDefinitions } from '@/services/customFieldService';
import type {
  Item,
  Shelf,
  Detection,
  Branch,
  Category,
  CustomFieldDefinition,
  Scanner,
} from '@packages/shared-types';

export function ItemDetailsPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeTenant } = useTenantSwitcher();
  const isAdmin = user?.role === 'root' || user?.role === 'admin';

  const title = useIndustryTranslation('pages.items.details.title');
  const description = useIndustryTranslation('pages.items.details.description');
  const loadingLabel = useIndustryTranslation('pages.items.list.loading');
  const errorLoad = useIndustryTranslation('pages.items.error.load');
  const categoryLabel = useIndustryTranslation('pages.items.form.category');
  const branchLabel = useIndustryTranslation('pages.items.form.branch');
  const shelfTableLabel = useIndustryTranslation('pages.items.details.table.shelf');
  const currentShelfLabel = useIndustryTranslation('pages.items.details.fields.currentShelf');
  const shelfAisleLabel = useIndustryTranslation('pages.shelves.fields.aisle');
  const shelfModuleLabel = useIndustryTranslation('pages.shelves.fields.module');
  const shelfRowLabel = useIndustryTranslation('pages.shelves.fields.row');
  const shelfCellLabel = useIndustryTranslation('pages.shelves.fields.cell');

  const pagination = usePaginationFromUrl();

  const [item, setItem] = useState<Item | null>(null);
  const [shelf, setShelf] = useState<Shelf | null>(null);
  const [detections, setDetections] = useState<Detection[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [scanners, setScanners] = useState<Scanner[]>([]);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetectionsLoading, setIsDetectionsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [isQrDialogOpen, setIsQrDialogOpen] = useState(false);
  const [barcodeDataUrl, setBarcodeDataUrl] = useState<string | null>(null);
  const [isBarcodeDialogOpen, setIsBarcodeDialogOpen] = useState(false);
  const [qrSize, setQrSize] = useState<'small' | 'medium' | 'big'>('medium');
  const [qrIncludeTitle, setQrIncludeTitle] = useState(true);
  const [qrIncludeSku, setQrIncludeSku] = useState(true);
  const [customFieldLabelByKey, setCustomFieldLabelByKey] = useState<
    Record<string, string>
  >({});

  const loadItem = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const [itemData, branchesData, categoriesData, scannersData, shelvesData] =
        await Promise.all([
          getItemById(id),
          listBranches(),
          listCategories(),
          listScanners(),
          listShelves(),
        ]);

      setItem(itemData);
      setBranches(branchesData);
      setCategories(categoriesData);
      setScanners(scannersData);
      setShelves(shelvesData);

      // Load shelf if item has currentShelfId
      if (itemData.currentShelfId) {
        try {
          const shelfData = await getShelfById(itemData.currentShelfId);
          setShelf(shelfData);
        } catch (err) {
          console.error('Failed to load shelf', err);
          // Shelf might not exist, continue without it
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : errorLoad;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [id, errorLoad, activeTenant?.id]);

  const loadDetections = useCallback(async () => {
    if (!id) return;
    setIsDetectionsLoading(true);
    try {
      const result = await getDetectionsByItemIdPaginated(
        id,
        pagination.page,
        pagination.limit,
      );
      setDetections(result.data);
      pagination.setTotal(result.total);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : errorLoad);
    } finally {
      setIsDetectionsLoading(false);
    }
  }, [id, pagination.page, pagination.limit, errorLoad, activeTenant?.id]);

  useEffect(() => {
    void loadItem();
  }, [loadItem]);

  useEffect(() => {
    void loadDetections();
  }, [loadDetections]);

  // Load custom field definitions to show labels (name) instead of key
  useEffect(() => {
    if (!item) {
      setCustomFieldLabelByKey({});
      return;
    }
    let cancelled = false;
    const load = async (): Promise<void> => {
      const [categoryDefs, tenantDefs] = await Promise.all([
        item.categoryId
          ? listCustomFieldDefinitions(item.categoryId)
          : Promise.resolve([]),
        listCustomFieldDefinitions(null),
      ]);
      if (cancelled) return;
      const keySet = new Set<string>();
      const merged: CustomFieldDefinition[] = [];
      for (const d of [...categoryDefs, ...tenantDefs]) {
        if (!keySet.has(d.key)) {
          keySet.add(d.key);
          merged.push(d);
        }
      }
      const labelByKey: Record<string, string> = {};
      for (const d of merged) {
        labelByKey[d.key] = d.name;
      }
      setCustomFieldLabelByKey(labelByKey);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [item?.id, item?.categoryId]);

  const qrUrl = item
    ? `${typeof window !== 'undefined' ? window.location.origin : 'https://app.trushelf.io'}/items/${item.id}`
    : '';

  useEffect(() => {
    if (item && isQrDialogOpen) {
      QRCode.toDataURL(qrUrl, { width: 256, margin: 2 })
        .then(setQrDataUrl)
        .catch(() => setQrDataUrl(null));
    } else {
      setQrDataUrl(null);
    }
  }, [item?.id, isQrDialogOpen, qrUrl]);

  const barcodeHeightPx = 80;
  useEffect(() => {
    if (!item || !isBarcodeDialogOpen) {
      setBarcodeDataUrl(null);
      return;
    }
    try {
      const canvas = document.createElement('canvas');
      const value = item.sku?.trim() || item.id;
      JsBarcode(canvas, value, {
        format: 'CODE128',
        width: 2,
        height: barcodeHeightPx,
        margin: 4,
        displayValue: false,
      });
      setBarcodeDataUrl(canvas.toDataURL('image/png'));
    } catch {
      setBarcodeDataUrl(null);
    }
  }, [item?.id, item?.sku, isBarcodeDialogOpen]);

  const qrSizePx = { small: 192, medium: 256, big: 320 }[qrSize];

  const handleDownloadQr = useCallback(async () => {
    if (!item) return;
    try {
      const qrUrl = `${window.location.origin}/items/${item.id}`;
      const qrImg = await QRCode.toDataURL(qrUrl, { width: qrSizePx, margin: 2 });
      if (!qrIncludeTitle && !qrIncludeSku) {
        const link = document.createElement('a');
        link.href = qrImg;
        link.download = `trushelf-item-${item.name.replace(/[^a-z0-9]/gi, '-')}-${item.id}.png`;
        link.click();
        return;
      }
      const padding = 16;
      const titleH = qrIncludeTitle ? 24 : 0;
      const skuH = qrIncludeSku && item.sku ? 18 : 0;
      const gap = 8;
      const canvasW = qrSizePx + padding * 2;
      const canvasH = padding + titleH + (titleH ? gap : 0) + skuH + (skuH ? gap : 0) + qrSizePx + padding;
      const canvas = document.createElement('canvas');
      canvas.width = canvasW;
      canvas.height = canvasH;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        const link = document.createElement('a');
        link.href = qrImg;
        link.download = `trushelf-item-${item.name.replace(/[^a-z0-9]/gi, '-')}-${item.id}.png`;
        link.click();
        return;
      }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasW, canvasH);
      ctx.fillStyle = '#000000';
      ctx.font = '600 18px system-ui, sans-serif';
      ctx.textAlign = 'center';
      let y = padding;
      if (qrIncludeTitle) {
        ctx.fillText(item.name, canvasW / 2, y + 18);
        y += titleH + gap;
      }
      if (qrIncludeSku && item.sku) {
        ctx.font = '13px system-ui, sans-serif';
        ctx.fillStyle = '#374151';
        ctx.fillText(`SKU: ${item.sku}`, canvasW / 2, y + 14);
        y += skuH + gap;
      }
      const img = new Image();
      img.src = qrImg;
      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          ctx.drawImage(img, padding, y, qrSizePx, qrSizePx);
          resolve();
        };
        img.onerror = reject;
      });
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `trushelf-item-${item.name.replace(/[^a-z0-9]/gi, '-')}-${item.id}.png`;
      link.click();
    } catch (err) {
      toast.error(t('pages.items.details.actions.downloadQrError'));
    }
  }, [item, qrSizePx, qrIncludeTitle, qrIncludeSku, t]);

  const handlePrintQr = useCallback(async () => {
    if (!item) return;
    try {
      const qrUrl = `${window.location.origin}/items/${item.id}`;
      const qrImg = await QRCode.toDataURL(qrUrl, { width: qrSizePx, margin: 2 });
      const skuLabel = t('pages.items.form.sku');
      const logoUrl = `${window.location.origin}/brand/trushelf-horizontal.svg`;
      const titleHtml = qrIncludeTitle ? `<h1>${item.name.replace(/</g, '&lt;')}</h1>` : '';
      const skuHtml = qrIncludeSku && item.sku ? `<p class="sku">${skuLabel}: ${item.sku.replace(/</g, '&lt;')}</p>` : '';
      const maxW = Math.max(320, qrSizePx + 48);
      const printHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${item.name} - TrueShelf</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: system-ui, sans-serif; background: white; padding: 20px; min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; }
            .cutting-area { border: 1px dashed #9ca3af; padding: 12px; position: relative; }
            .content { max-width: ${maxW}px; text-align: center; }
            h1 { font-size: 18px; font-weight: 600; color: #000; margin-bottom: 4px; }
            .sku { font-size: 13px; color: #374151; margin-bottom: 12px; }
            .qr { border: 1px solid #e5e7eb; border-radius: 6px; padding: 6px 8px 4px; background: white; margin-bottom: 8px; }
            .qr img { display: block; margin: 0 auto; }
            .qr .logo { height: 14px; opacity: 0.7; margin-top: 4px; display: block; margin-left: auto; margin-right: auto; }
          </style>
        </head>
        <body>
          <div class="cutting-area">
            <div class="content">
              ${titleHtml}
              ${skuHtml}
              <div class="qr">
                <img src="${qrImg}" alt="QR Code" width="${qrSizePx}" height="${qrSizePx}" />
                <img src="${logoUrl}" alt="TrueShelf" class="logo" />
              </div>
            </div>
          </div>
        </body>
      </html>
    `;
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.style.left = '-9999px';
      document.body.appendChild(iframe);
      const iframeDoc = iframe.contentDocument ?? iframe.contentWindow?.document;
      if (!iframeDoc) {
        document.body.removeChild(iframe);
        toast.error(t('pages.items.details.actions.printQrError'));
        return;
      }
      iframeDoc.open();
      iframeDoc.write(printHtml);
      iframeDoc.close();
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch {
          toast.error(t('pages.items.details.actions.printQrError'));
        }
        setTimeout(() => {
          if (iframe.parentNode) document.body.removeChild(iframe);
        }, 1000);
      }, 300);
    } catch {
      toast.error(t('pages.items.details.actions.printQrError'));
    }
  }, [item, qrSizePx, qrIncludeTitle, qrIncludeSku, t]);

  const handlePrintBarcode = useCallback(() => {
    if (!item || !barcodeDataUrl) return;
    try {
      const skuLabel = t('pages.items.form.sku');
      const logoUrl = `${window.location.origin}/brand/trushelf-horizontal.svg`;
      const titleHtml = qrIncludeTitle ? `<h1>${item.name.replace(/</g, '&lt;')}</h1>` : '';
      const skuHtml = qrIncludeSku && item.sku ? `<p class="sku">${skuLabel}: ${item.sku.replace(/</g, '&lt;')}</p>` : '';
      const printHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${item.name} - TrueShelf</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: system-ui, sans-serif; background: white; padding: 20px; min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; }
            .cutting-area { border: 1px dashed #9ca3af; padding: 12px; position: relative; }
            .content { max-width: 320px; text-align: center; }
            h1 { font-size: 18px; font-weight: 600; color: #000; margin-bottom: 4px; }
            .sku { font-size: 13px; color: #374151; margin-bottom: 12px; }
            .barcode { border: 1px solid #e5e7eb; border-radius: 6px; padding: 6px 8px 4px; background: white; margin-bottom: 8px; }
            .barcode img { display: block; margin: 0 auto; max-width: 100%; height: auto; }
            .barcode .logo { height: 14px; opacity: 0.7; margin-top: 4px; display: block; margin-left: auto; margin-right: auto; }
          </style>
        </head>
        <body>
          <div class="cutting-area">
            <div class="content">
              ${titleHtml}
              ${skuHtml}
              <div class="barcode">
                <img src="${barcodeDataUrl}" alt="Barcode" />
                <img src="${logoUrl}" alt="TrueShelf" class="logo" />
              </div>
            </div>
          </div>
        </body>
      </html>
    `;
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.style.left = '-9999px';
      document.body.appendChild(iframe);
      const iframeDoc = iframe.contentDocument ?? iframe.contentWindow?.document;
      if (!iframeDoc) {
        document.body.removeChild(iframe);
        toast.error(t('pages.items.details.actions.printBarcodeError'));
        return;
      }
      iframeDoc.open();
      iframeDoc.write(printHtml);
      iframeDoc.close();
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch {
          toast.error(t('pages.items.details.actions.printBarcodeError'));
        }
        setTimeout(() => {
          if (iframe.parentNode) document.body.removeChild(iframe);
        }, 1000);
      }, 300);
    } catch {
      toast.error(t('pages.items.details.actions.printBarcodeError'));
    }
  }, [item, barcodeDataUrl, qrIncludeTitle, qrIncludeSku, t]);

  const handleEdit = (): void => {
    if (!item) return;
    navigate(`/items/${item.id}/edit`);
  };

  const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  const formatDateTime = (date: Date | string): string => {
    return new Date(date).toLocaleString();
  };

  const getScanTypeLabel = (scanType: string): string => {
    return t(`pages.items.details.scanTypes.${scanType}`, { defaultValue: scanType });
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={title} description={description} />
        <div className="flex flex-col items-center gap-3 py-8">
          <LoadingLogo size="sm" />
          <span className="text-sm text-muted-foreground">{loadingLabel}</span>
        </div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={title} description={description} onBack={() => navigate(-1)} />
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="p-4">
            <p className="text-sm text-destructive">
              {error || t('pages.items.error.notFound')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const category = categories.find((c) => c.id === item.categoryId);
  const branch = branches.find((b) => b.id === item.branchId);

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={item.name}
        description={description}
        onBack={() => navigate(-1)}
        actions={
          <div className="flex gap-2">
            <Dialog open={isQrDialogOpen} onOpenChange={setIsQrDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <QrCode className="mr-2 h-4 w-4" />
                  {t('pages.items.details.actions.viewQr')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('pages.items.details.actions.viewQr')}</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center gap-4 py-4">
                  {qrDataUrl && (
                    <img
                      src={qrDataUrl}
                      alt="QR Code"
                      className="rounded-lg border bg-white p-2"
                      width={256}
                      height={256}
                    />
                  )}
                  <div className="w-full space-y-3 border-t pt-4">
                    <div>
                      <Label className="text-sm font-medium">
                        {t('pages.items.details.qr.sizeLabel')}
                      </Label>
                      <div className="mt-1.5 flex gap-2">
                        {(['small', 'medium', 'big'] as const).map((size) => (
                          <Button
                            key={size}
                            type="button"
                            variant={qrSize === size ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setQrSize(size)}
                          >
                            {t(`pages.items.details.qr.size${size.charAt(0).toUpperCase() + size.slice(1)}`)}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="qr-include-title"
                          checked={qrIncludeTitle}
                          onCheckedChange={(checked) =>
                            setQrIncludeTitle(checked === true)
                          }
                        />
                        <Label
                          htmlFor="qr-include-title"
                          className="cursor-pointer text-sm font-normal"
                        >
                          {t('pages.items.details.qr.includeTitle')}
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="qr-include-sku"
                          checked={qrIncludeSku}
                          onCheckedChange={(checked) =>
                            setQrIncludeSku(checked === true)
                          }
                        />
                        <Label
                          htmlFor="qr-include-sku"
                          className="cursor-pointer text-sm font-normal"
                        >
                          {t('pages.items.details.qr.includeSku')}
                        </Label>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleDownloadQr}
                      disabled={!qrDataUrl}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      {t('pages.items.details.actions.downloadQr')}
                    </Button>
                    <Button onClick={() => void handlePrintQr()}>
                      <Printer className="mr-2 h-4 w-4" />
                      {t('pages.items.details.actions.printQr')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={isBarcodeDialogOpen} onOpenChange={setIsBarcodeDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Barcode className="mr-2 h-4 w-4" />
                  {t('pages.items.details.actions.viewBarcode')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('pages.items.details.actions.viewBarcode')}</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center gap-4 py-4">
                  {barcodeDataUrl && (
                    <img
                      src={barcodeDataUrl}
                      alt={t('pages.items.details.actions.barcodeAlt')}
                      className="rounded-lg border bg-white p-2"
                      style={{ height: barcodeHeightPx, width: 'auto', maxWidth: '100%' }}
                    />
                  )}
                  <div className="w-full space-y-3 border-t pt-4">
                    <div className="flex gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="barcode-include-title"
                          checked={qrIncludeTitle}
                          onCheckedChange={(checked) =>
                            setQrIncludeTitle(checked === true)
                          }
                        />
                        <Label
                          htmlFor="barcode-include-title"
                          className="cursor-pointer text-sm font-normal"
                        >
                          {t('pages.items.details.qr.includeTitle')}
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="barcode-include-sku"
                          checked={qrIncludeSku}
                          onCheckedChange={(checked) =>
                            setQrIncludeSku(checked === true)
                          }
                        />
                        <Label
                          htmlFor="barcode-include-sku"
                          className="cursor-pointer text-sm font-normal"
                        >
                          {t('pages.items.details.qr.includeSku')}
                        </Label>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => void handlePrintBarcode()}
                      disabled={!barcodeDataUrl}
                    >
                      <Printer className="mr-2 h-4 w-4" />
                      {t('pages.items.details.actions.printQr')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            {isAdmin && (
              <Button variant="outline" onClick={handleEdit}>
                <Edit className="mr-2 h-4 w-4" />
                {t('pages.items.details.actions.edit')}
              </Button>
            )}
          </div>
        }
      />

      <div className="grid gap-6 md:grid-cols-2">
        {/* Item Information */}
        <Card>
          <CardContent className="p-6">
            <h2 className="mb-4 text-lg font-semibold">
              {t('pages.items.details.sections.itemInfo')}
            </h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('pages.items.form.name')}
                </p>
                <p className="mt-1 text-sm">{item.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('pages.items.form.sku')}
                </p>
                <p className="mt-1 font-mono text-sm">{item.sku}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('pages.items.form.rfidTag')}
                </p>
                <p className="mt-1 font-mono text-xs">{item.rfidTagId ?? '-'}</p>
              </div>
              {item.description && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t('pages.items.form.description')}
                  </p>
                  <p className="mt-1 text-sm">{item.description}</p>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {categoryLabel}
                </p>
                <p className="mt-1 text-sm">
                  {(() => {
                    const breadcrumb = (item.category as Category & { categoryBreadcrumb?: string })
                      ?.categoryBreadcrumb ?? category?.name ?? '-';
                    if (typeof breadcrumb === 'string' && breadcrumb.startsWith('All > ')) {
                      return `${t('pages.items.details.fields.categoryBreadcrumbRoot')}${breadcrumb.slice(3)}`;
                    }
                    return breadcrumb;
                  })()}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {branchLabel}
                </p>
                <p className="mt-1 text-sm">{branch?.name || '-'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('pages.items.details.fields.createdAt')}
                </p>
                <p className="mt-1 text-sm">{formatDate(item.createdAt)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('pages.items.details.fields.updatedAt')}
                </p>
                <p className="mt-1 text-sm">{formatDate(item.updatedAt)}</p>
              </div>
              {item.customFieldValues &&
                Object.keys(item.customFieldValues).length > 0 && (
                  <div className="border-t pt-4 mt-4 space-y-4">
                    <p className="text-sm font-semibold">
                      {t('pages.items.details.sections.customFields')}
                    </p>
                    {Object.entries(item.customFieldValues).map(([key, val]) => (
                      <div key={key}>
                        <p className="text-sm font-medium text-muted-foreground">
                          {customFieldLabelByKey[key] ?? key.replace(/_/g, ' ')}
                        </p>
                        <p className="mt-1 text-sm">
                          {typeof val === 'string' && val.startsWith('http') ? (
                            <a
                              href={val}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              {val}
                            </a>
                          ) : (
                            String(val || '—')
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </CardContent>
        </Card>

        {/* Current Location */}
        <Card>
          <CardContent className="p-6">
            <h2 className="mb-4 text-lg font-semibold">
              {t('pages.items.details.sections.currentLocation')}
            </h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {currentShelfLabel}
                </p>
                <p className="mt-1 text-sm">{shelf?.name || t('pages.items.details.notAssigned')}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('pages.items.details.fields.zone')}
                </p>
                <p className="mt-1 text-sm">{shelf?.zone ?? '—'}</p>
              </div>
              {shelf?.aisle != null && shelf.aisle !== '' && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {shelfAisleLabel}
                  </p>
                  <p className="mt-1 text-sm">{shelf.aisle}</p>
                </div>
              )}
              {shelf?.module != null && shelf.module !== '' && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {shelfModuleLabel}
                  </p>
                  <p className="mt-1 text-sm">{shelf.module}</p>
                </div>
              )}
              {item.currentPositionCell != null && item.currentPositionCell !== '' && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t('pages.items.details.fields.position')}
                  </p>
                  <p className="mt-1 text-sm">
                    {(() => {
                      const key = item.currentPositionCell ?? '';
                      const dashIdx = key.indexOf('-');
                      const row = dashIdx <= 0 ? key : key.slice(0, dashIdx);
                      const cell = dashIdx < 0 ? '' : key.slice(dashIdx + 1);
                      return row && cell
                        ? `${shelfRowLabel} ${row}, ${shelfCellLabel} ${cell}`
                        : key;
                    })()}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {branchLabel}
                </p>
                <p className="mt-1 text-sm">{branch?.name || '—'}</p>
              </div>
              {shelf?.scannerId != null && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t('pages.items.details.fields.scanner')}
                  </p>
                  <p className="mt-1 text-sm">
                    {scanners.find((s) => s.id === shelf.scannerId)?.name ?? '—'}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('pages.items.details.fields.lastDetected')}
                </p>
                <p className="mt-1 text-sm">{formatDate(item.lastDetectedAt)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detection History */}
      <Card>
        <CardContent className="p-6">
          <h2 className="mb-4 text-lg font-semibold">
            {t('pages.items.details.sections.detectionHistory')}
          </h2>
          {isDetectionsLoading ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <LoadingLogo size="sm" />
              <span className="text-sm text-muted-foreground">{loadingLabel}</span>
            </div>
          ) : detections.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {t('pages.items.details.empty.detections')}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('pages.items.details.table.dateTime')}</TableHead>
                    <TableHead>{t('pages.items.details.table.scanner')}</TableHead>
                    <TableHead>{shelfTableLabel}</TableHead>
                    <TableHead>{t('pages.items.details.table.signalStrength')}</TableHead>
                    <TableHead>{t('pages.items.details.table.scanType')}</TableHead>
                    <TableHead>{t('pages.items.details.table.user')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detections.map((detection) => (
                    <TableRow key={detection.id}>
                      <TableCell className="text-sm">
                        {formatDateTime(detection.detectedAt)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {scanners.find((s) => s.id === detection.scannerId)?.name || '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {detection.shelfId
                          ? shelves.find((s) => s.id === detection.shelfId)?.name || '-'
                          : '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {detection.signalStrength !== null && detection.signalStrength !== undefined
                          ? `${detection.signalStrength} dBm`
                          : '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {getScanTypeLabel(detection.scanType)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {(() => {
                          // Check if user relation is included in response
                          const detectionWithUser = detection as Detection & {
                            user?: { firstName?: string; lastName?: string; email: string } | null;
                          };
                          if (detectionWithUser.user) {
                            const name = `${detectionWithUser.user.firstName || ''} ${detectionWithUser.user.lastName || ''}`.trim();
                            return name || detectionWithUser.user.email;
                          }
                          return '-';
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                pagination={pagination}
                rowsPerPageLabel={t('pages.common.pagination.rowsPerPage')}
                pageOfLabel={t('pages.common.pagination.pageOf', {
                  current: pagination.page,
                  total: pagination.totalPages,
                })}
                onRefresh={() => void loadDetections()}
                isRefreshing={isDetectionsLoading}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
